import React, { FC, memo, useCallback, useState, useMemo } from 'react';
import ModalScreen from 'components/Modals/ModalScreen';
import { Formik, Form } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import {
  addModifier,
  editModifier,
  removeModifier,
  closeModals,
} from 'data/cacheHelpers/menu';

import AskBeforeDelete from 'components/Modals/AskBeforeDelete';
import {
  Container,
  SectionTitle,
  Details,
  ApliesTo,
  Modifiers,
  ActionBar,
  Actions,
  ActionButton,
  Button,
} from './styles';
import { Props } from './types';
import { validate } from './constants';

const ModifierModal: FC<Props> = ({
  brandId,
  previousData,
  items,
  isProcessing,
}) => {
  const [isConfirmRemoveOpen, setConfirmRemove] = useState(false);
  const { id } = previousData;
  const isNew = id === undefined;

  const handleSubmit = useCallback(
    (values: any) => {
      if (isNew) {
        addModifier(brandId, {
          ...values,
          id: uuidv4(),
        });
      } else {
        editModifier(brandId, values);
      }
      closeModals(brandId);
    },
    [isNew, brandId],
  );

  const closeModal = useCallback(() => closeModals(brandId), [brandId]);

  const handleRemoveItem = useCallback(() => {
    removeModifier(brandId, id);
    closeModals(brandId);
  }, [brandId, id]);

  const askConfirmRemove = useCallback(() => setConfirmRemove(true), []);
  const closeConfirmRemove = useCallback(() => setConfirmRemove(false), []);

  const initialValues = useMemo(
    () => ({
      id: previousData.id || undefined,
      name: previousData.name || '',
      usedIn: previousData.usedIn || [],
      items: previousData.items || [],
      choices: {
        minimum: previousData.choices ? previousData.choices.minimum : 0,
        maximum: previousData.choices ? previousData.choices.maximum : 0,
      },
      temporalItems: previousData.temporalItems || [],
    }),
    [previousData],
  );

  return (
    <ModalScreen
      open
      title={`${isNew ? 'Add' : 'Edit'} modifier set`}
      handleClose={closeModal}
      showCloseButtonHeader={false}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
      >
        {({ values }) => (
          <Form noValidate>
            <Container>
              <SectionTitle>Set details</SectionTitle>
              <Details
                maxItems={values.temporalItems.length}
                isProcessing={isProcessing}
              />
              <SectionTitle>Applies to</SectionTitle>
              <ApliesTo isProcessing={isProcessing} brandId={brandId} />
              <SectionTitle>Modifiers</SectionTitle>
              <Modifiers
                isProcessing={isProcessing}
                brandId={brandId}
                items={items}
              />
            </Container>
            <ActionBar>
              <Actions>
                <ActionButton
                  variant="delete"
                  type="button"
                  onClick={askConfirmRemove}
                  disabled={isProcessing || isNew}
                />
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={closeModal}
                  style={{ marginLeft: 'auto' }}
                >
                  Cancel
                </Button>
                <Button color="secondary" type="submit" disabled={isProcessing}>
                  Save
                </Button>
              </Actions>
            </ActionBar>
            {isConfirmRemoveOpen && !isNew && (
              <AskBeforeDelete
                message="Are you sure you want to delete this modifier?"
                handleRemove={handleRemoveItem}
                handleClose={closeConfirmRemove}
              />
            )}
          </Form>
        )}
      </Formik>
    </ModalScreen>
  );
};

export default memo(ModifierModal);

// TODO: TFR-29 add story to component
